<template>
  <IQOS :enabled="is_active" :title="$t('settings.two_factor.title')">
    <div v-if="is_active" class="mt-16">
      <div class="auth-stage" v-if="ui_stage == 'phone'">
        <BaseInput
          placeholder="+XX-YYY-YY-YY"
          v-model="phone"
          :readonly="sms_sent"
          fullWidth
          :mask="countryBasedPhoneMask"
        />
        <p class="iqos-comment qrw-paragraph-md mt-16">
          {{ $t("settings.two_factor.set_phone_desc") }}
        </p>
        <BaseDivider color="v1-grey-5" class="mt-16" />
        <div class="iqos-flex-right mt-16">
          <BaseButton
            variant="secondary"
            color="v1-grey-3"
            size="md"
            @click="sendSMS"
            :disabled="!phone"
            v-if="!sms_sent"
            >{{ $t("settings.two_factor.send_sms") }}</BaseButton
          >
        </div>
      </div>
      <div class="auth-stage" v-if="ui_stage == 'code'">
        <BaseInput
          :placeholder="$t('settings.two_factor.confirm_code_ph')"
          v-model="confirm_code"
          :readonly="false"
          :mask="'######'"
          fullWidth
          :class="{
            'iqos-group-item': false,
            'invalid-code': !!error,
          }"
        />
        <p class="iqos-comment qrw-paragraph-md mt-16">
          {{ $t("settings.two_factor.set_code_desc") }}
        </p>

        <BaseDivider color="v1-grey-5" class="mt-16" />
        <div class="auth-buttons mt-16">
          <BaseButton
            variant="secondary"
            color="v1-grey-3"
            class="w-100 mr-16"
            size="md"
            @click="changePhone"
            >{{ $t("settings.two_factor.change_phone") }}</BaseButton
          >
          <BaseButton
            variant="secondary"
            color="v1-grey-3"
            disabled
            class="w-100"
            size="md"
            @click="changePhone"
            >{{ $t("settings.two_factor.confirm_phone") }}</BaseButton
          >
        </div>
      </div>
      <div class="auth-stage" v-if="ui_stage == 'confirmed'">
        <BaseInput 
          class="w-100"
          disabled
          :value="hiddenTwofactorPhone" 
          v-if="twofactor_enabled" />

        <p class="iqos-comment qrw-paragraph-md mt-16">
          {{ $t("settings.two_factor.is_active") }}
        </p>

        <BaseDivider color="v1-grey-5" class="mt-16" />

        <BaseButton class="w-100 mt-16" size="md" variant="secondary" color="v1-grey-3" @click="changePhone">{{
          $t("settings.two_factor.change_phone")
        }}</BaseButton>
      </div>
    </div>

    <p class="iqos-comment qrw-paragraph-md mt-16" v-if="!is_active">
      {{ $t("settings.two_factor.description") }}
    </p>

    <template #actions>
      <IQOSToggle :value="is_active" @input="onToggleChange" />
    </template>
  </IQOS>
</template>

<script>
import TwoFactorAuthModal from "@/components/modals/TwoFactorAuthModal.vue";

import AuthAPI from "@/api/auth";

export default {
  data() {
    return {
      editing: false,
      phone: null,

      sms_sent: false,
      confirm_code: null,
      change_token: false,

      is_active: false,

      error: null,

      ui_stage: "idle",
    };
  },
  watch: {
    confirm_code() {
      if (this.confirm_code.length == 6) this.tryToConfirm();
    },
  },
  methods: {
    onToggle() {
      this.editing = true;

      this.confirm_code = null;
      this.sms_sent = false;

      if (this.twofactor_phone) {
        this.phone = this.twofactor_phone;
      }
    },
    onToggleChange(v) {
      if (!v && this.twofactor_enabled) {
        this.phone = this.twofactor_phone;
        this.sendSMS();
        return;
      }

      if (v && !this.twofactor_enabled) {
        this.ui_stage = "phone";
      }

      this.is_active = v;
    },
    changePhone() {
      this.ui_stage = "phone";
      this.sms_sent = false;
      this.confirm_code = null;
    },
    sendSMS() {
      if (!this.phone || this.phone.length < 12) {
        return;
      }

      const raw_phone = this.phone.split("-").join("");

      AuthAPI.changeTwoFactorAuth(!this.twofactor_enabled, raw_phone)
        .then((data) => {
          this.sms_sent = true;
          this.change_token = data.token;
          this.confirm_code = null;
          this.ui_stage = "code";
        })
        .catch((error) => {
          this.$store.dispatch("showError", "auth_error");
        });
    },
    tryToConfirm() {
      if (!this.confirm_code) return;

      AuthAPI.confirmTwoFactorAuthChange(this.change_token, this.confirm_code)
        .then((data) => {
          this.$store.commit("setOwnerTwoFactorAuth", !this.twofactor_enabled);
          this.editing = false;
          this.ui_stage = this.twofactor_enabled ? "confirmed" : "idle";
          this.is_active = this.twofactor_enabled;
        })
        .catch((error) => {
          this.error = error;
          this.confirm_code = null;
        });
    },
  },
  mounted() {
    if (this.twofactor_enabled) {
      this.is_active = true;
      this.ui_stage = "confirmed";
    }
  },
  computed: {
    owner() {
      return this.$store.state.owner;
    },
    twofactor_enabled() {
      return this.owner.two_factor_auth.enabled;
    },
    twofactor_phone() {
      return this.owner.two_factor_auth.phone;
    },
    hiddenTwofactorPhone() {
      if (!this.twofactor_phone) return "";

      return (
        this.twofactor_phone.substring(0, 3) +
        "*******" +
        this.twofactor_phone.substring(this.twofactor_phone.length - 3)
      );
    },
    countryBasedPhoneMask() {
      const PHONE_MASKS = {
        ua: "+38-###-###-##-##",
        ru: "+7-###-###-##-##",
      };

      return PHONE_MASKS[this.$store.state.owner.country.toLowerCase()];
    },
  },
  components: {
    TwoFactorAuthModal,
  },
};
</script>

<style scoped>
.to-right {
}

.auth-buttons {
  display: flex;
  justify-content: space-between;
}

.invalid-code {
  border: 2px solid #e2223b;
}

.auth-stage {
  text-align: center;
}
</style>
